import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const Navbar = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location]);

    const navLinks = [
        { name: 'Home', path: '/' },
        { name: 'About', path: '/about' },
        { name: 'Solutions', path: '/services' },
        { name: 'Service Delivery', path: '/solutions' },
        { name: 'Contact', path: '/contact' },
    ];

    const isActive = (path) => location.pathname === path;

    return (
        <nav
            data-testid="main-navbar"
            className={`fixed top-0 w-full z-50 transition-all duration-300 ${
                isScrolled
                    ? 'bg-white/90 backdrop-blur-md border-b border-slate-100/50 shadow-sm'
                    : 'bg-white shadow-sm'
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 lg:px-16 xl:px-24">
                <div className="flex items-center justify-between h-20 sm:h-24 lg:h-28">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-2 sm:gap-3 flex-shrink-0" data-testid="navbar-logo">
                        <img
                            src="https://customer-assets.emergentagent.com/job_network-excellence/artifacts/9goyrt37_Logo%20With%20Shadow.png"
                            alt="Vanfrank Logo"
                            className="h-12 sm:h-16 lg:h-20 w-auto rounded-lg"
                        />
                        <span className={`font-bold text-xl sm:text-2xl lg:text-3xl tracking-tight text-slate-900`}>
                            Vanfrank
                        </span>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden lg:flex items-center gap-4 xl:gap-8">
                        {navLinks.map((link) => (
                            <Link
                                key={link.path}
                                to={link.path}
                                data-testid={`nav-link-${link.name.toLowerCase()}`}
                                className={`font-medium transition-colors duration-200 ${
                                    isActive(link.path)
                                        ? 'text-blue-500'
                                        : 'text-slate-600 hover:text-slate-900'
                                }`}
                            >
                                {link.name}
                            </Link>
                        ))}
                        <Link
                            to="/contact"
                            data-testid="navbar-cta"
                            className="px-6 py-2.5 rounded-full font-semibold transition-all duration-300 bg-slate-900 text-white hover:bg-slate-800 shadow-[0_0_20px_rgba(15,23,42,0.2)]"
                        >
                            Get In Touch
                        </Link>
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        data-testid="mobile-menu-button"
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        className="lg:hidden p-2 rounded-lg transition-colors text-slate-900 hover:bg-slate-100"
                    >
                        {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </div>

            {/* Mobile Menu */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="lg:hidden bg-white border-t border-slate-100"
                        data-testid="mobile-menu"
                    >
                        <div className="px-6 py-4 space-y-2">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.path}
                                    to={link.path}
                                    data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                                    className={`block py-3 px-4 rounded-lg font-medium transition-colors ${
                                        isActive(link.path)
                                            ? 'bg-blue-50 text-blue-600'
                                            : 'text-slate-600 hover:bg-slate-50'
                                    }`}
                                >
                                    {link.name}
                                </Link>
                            ))}
                            <Link
                                to="/contact"
                                data-testid="mobile-cta"
                                className="block mt-4 py-3 px-4 bg-slate-900 text-white text-center rounded-full font-semibold"
                            >
                                Get In Touch
                            </Link>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    );
};
